/**
 * ⟦C57 · A-174 · C62 lot arbre · A-179⟧ **« Les dessous »** : le seul bouton, et le seul dialogue, qui portent
 * ce que l'écran ne dit plus.
 *
 * Matt, 02/10/2026 : *« Il y a trop de trucs écrits partout, on ne lit plus rien à la fin. Il
 * faut laisser des boutons pour voir "les dessous", dans des détails de journal, mais tout doit
 * être simplifié : ne mettre que les mots ou petites phrases courtes importantes. »*
 *
 * ## La règle (`game/dessous.ts`)
 *
 * Par défaut l'écran garde un titre court (5 mots), les chiffres qui comptent et au plus une
 * phrase courte (12 mots) par bloc. Tout le reste : explications, calculs, détail des effets,
 * conseils longs… passe ICI. **On ne supprime aucune information : elle change de place.** Les
 * textes longs vivent toujours dans `content/` (et dans le code des écrans), seulement ils
 * s'ouvrent à la demande.
 *
 * ## Le pictogramme : un « ? », plus le 📰 du Journal (C62 · A-179)
 *
 * Matt, 21/10/2026 : l'audit « regard neuf » a trouvé le même 📰 sur le bouton du Journal ET sur chaque
 * « Les dessous » (51 dans l'Arbre) : le joueur apprenait à l'ignorer, y compris quand il cachait l'information
 * utile. Le bouton porte désormais un petit « ? » carré, violet, dans la police pixel du jeu. **Trois règles
 * qui vont avec** (le composant n'en tient qu'une, les écrans tiennent les deux autres) :
 *
 * 1. le « ? » n'est plus le 📰 : le Journal garde SON pictogramme, il ne se confond plus avec une aide ;
 * 2. **au plus un « ? » par fenêtre ou par carte** : une liste d'éléments (les nœuds de l'Arbre) ouvre le
 *    détail d'un élément quand on clique l'élément lui-même (`DialogueDessous`, ci-dessous), pas par un
 *    « ? » par ligne ;
 * 3. **jamais un dessous qui redit mot pour mot ce que l'écran écrit déjà** : s'il n'a rien de plus à
 *    dire, il n'existe pas.
 *
 * ## Un seul composant pour tout le jeu
 *
 * Deux variantes, un seul dialogue :
 *
 * - `variante="texte"` (par défaut) : un petit bouton « ? Les dessous », pour un écran ou un
 *   bloc qui a UNE chose à ouvrir (le panneau du marché, l'article d'un guide) ;
 * - `variante="icone"` : le seul « ? », pour une ligne ou un bandeau où répéter « Les dessous » serait
 *   précisément du texte en trop. Le nom accessible reste « Les dessous : <titre> ».
 *
 * Le dialogue est une `Fenetre` dans le style du Journal (bandeau violet « ? Les dessous », corps
 * tramé, lignes à icône séparées d'un pointillé comme les entrées du journal), posée sur un
 * `.overlay` exactement comme `AppDialog` : **jamais `window.alert`/`confirm`/`prompt`**
 * (CLAUDE.md). Il se rend dans `#app` par un portail : un panneau (`.panneau-fond`, z-index 40)
 * ou une fenêtre de la chambre peuvent le contenir sans que leur propre contexte d'empilement
 * ne l'attrape (le voile est à 60, au-dessus).
 *
 * `DialogueDessous` est ce dialogue SEUL, commandé de l'extérieur (`onFermer`) : c'est lui qu'ouvre un clic sur
 * un élément de liste. `LesDessous` n'est que le bouton « ? » qui en pilote un.
 *
 * ## Le clavier
 *
 * Échap ferme CE dialogue et seulement lui : l'écouteur est posé en **phase de capture** et
 * arrête la propagation, sans quoi le `Panneau` dessous (qui écoute Échap sur `document`) se
 * refermerait en même temps. Tab reste dans la boîte (même piège à focus qu'`AppDialog`), et à
 * la fermeture le focus revient sur ce qui l'a ouvert (le bouton, ou l'élément cliqué).
 *
 * ## Et le Journal
 *
 * Quand un détail raconte **un fait de la partie** (une issue d'événement, un palier franchi),
 * il rejoint aussi le Journal : c'est `game/journal.ts` qui écrit l'entrée, pas ce composant. Ici
 * on n'ouvre que de la lecture.
 */

import { useEffect, useRef, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import { Fenetre } from "./Fenetre";
import { useSfx } from "./SoundProvider";

export interface DialogueDessousProps {
  /** Ce dont on ouvre les dessous : le nom du nœud, de l'offre, de l'article. Il titre le contenu. */
  titre: string;
  /** Ferme le dialogue : l'appelant met son état à « fermé ». */
  onFermer(): void;
  /**
   * Ce qui reprend le focus à la fermeture. Omis : l'élément qui avait le focus à l'ouverture (le bouton
   * cliqué, ou l'élément de liste). Safari ne donne pas le focus à un bouton au clic : `LesDessous` passe donc
   * son bouton explicitement.
   */
  retour?: RefObject<HTMLElement | null>;
  /** Le détail : `DessousBloc`, `DessousLignes`, ou n'importe quel JSX de lecture. */
  children: ReactNode;
}

/**
 * ⟦C62 · lot arbre · A-179⟧ **Le dialogue « Les dessous », seul**, commandé de l'extérieur. Un clic sur un nœud de
 * l'Arbre l'ouvre sans qu'il y ait un « ? » par nœud ; `LesDessous` le pilote derrière son bouton. Même fenêtre,
 * même clavier (Échap ferme ce dialogue seul, Tab reste dedans, le focus revient à l'ouvreur).
 */
export function DialogueDessous({ titre, onFermer, retour, children }: DialogueDessousProps) {
  const boite = useRef<HTMLDivElement>(null);
  const ouvreur = useRef<HTMLElement | null>(
    typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null,
  );
  const sfx = useSfx();
  // Les rappels changent à chaque rendu de l'appelant ; l'écouteur clavier, lui, est posé une seule fois.
  const fermeture = useRef(onFermer);
  fermeture.current = onFermer;

  function fermer() {
    sfx("dialog_close");
    fermeture.current();
    const cible = retour?.current ?? ouvreur.current;
    if (cible && document.contains(cible)) cible.focus();
  }

  useEffect(() => {
    sfx("dialog_open");
    boite.current?.querySelector<HTMLElement>(".fenetre-fermer-croix")?.focus();
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Seulement CE dialogue : le panneau dessous écoute aussi Échap sur `document`.
        e.stopPropagation();
        e.preventDefault();
        fermer();
        return;
      }
      if (e.key !== "Tab" || !boite.current) return;
      const focusables = boite.current.querySelectorAll<HTMLElement>(
        "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      );
      if (focusables.length === 0) return;
      e.stopPropagation();
      const premier = focusables[0]!;
      const dernier = focusables[focusables.length - 1]!;
      const actif = document.activeElement;
      if (e.shiftKey && (actif === premier || !boite.current.contains(actif))) {
        e.preventDefault();
        dernier.focus();
      } else if (!e.shiftKey && (actif === dernier || !boite.current.contains(actif))) {
        e.preventDefault();
        premier.focus();
      }
    };
    document.addEventListener("keydown", auClavier, true);
    return () => document.removeEventListener("keydown", auClavier, true);
    // `fermer` ne dépend que de refs et de `sfx` : un seul montage par ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div className="overlay overlay-dessous" role="presentation">
      <div className="dialogue-fenetre dialogue-fenetre-large" ref={boite}>
        <Fenetre
          titre="Les dessous"
          icone="?"
          role="dialog"
          ariaModal
          className="fenetre-dialogue fenetre-dessous"
          onFermer={fermer}
          pied={
            <button type="button" className="bouton primaire" onClick={fermer}>
              Fermer
            </button>
          }
        >
          <h2 className="dessous-sujet">{titre}</h2>
          <div className="dessous-corps">{children}</div>
        </Fenetre>
      </div>
    </div>,
    document.getElementById("app") ?? document.body,
  );
}

export interface LesDessousProps {
  /** Ce dont on ouvre les dessous : le nom du nœud, de l'offre, de l'article. Il titre le contenu. */
  titre: string;
  /** `texte` (« ? Les dessous ») ou `icone` (le seul « ? », pour une ligne ou un bandeau). */
  variante?: "texte" | "icone";
  /** Classe additionnelle sur le bouton, pour le placer dans la mise en page de l'écran. */
  className?: string;
  /** Le détail : `DessousBloc`, `DessousLignes`, ou n'importe quel JSX de lecture. */
  children: ReactNode;
}

export function LesDessous({ titre, variante = "texte", className, children }: LesDessousProps) {
  const [ouvert, setOuvert] = useState(false);
  const declencheur = useRef<HTMLButtonElement>(null);

  // Le bouton discret du jeu (crème, biseau pixel), en petit : un bouton de lecture, jamais une action.
  const classes = `bouton discret bouton-dessous${variante === "icone" ? " bouton-dessous-icone" : ""}${className ? ` ${className}` : ""}`;

  return (
    <>
      <button
        ref={declencheur}
        type="button"
        className={classes}
        aria-haspopup="dialog"
        aria-expanded={ouvert}
        aria-label={variante === "icone" ? `Les dessous : ${titre}` : undefined}
        title="Les dessous"
        onClick={() => setOuvert(true)}
      >
        <span className="dessous-point" aria-hidden="true">
          ?
        </span>
        {variante === "texte" ? " Les dessous" : null}
      </button>
      {ouvert ? (
        <DialogueDessous titre={titre} onFermer={() => setOuvert(false)} retour={declencheur}>
          {children}
        </DialogueDessous>
      ) : null}
    </>
  );
}

/** Un bloc du détail : un petit titre (facultatif), puis ce qu'on veut lire. */
export function DessousBloc({ titre, children }: { titre?: string; children: ReactNode }) {
  return (
    <section className="dessous-bloc">
      {titre ? <h3 className="dessous-bloc-titre">{titre}</h3> : null}
      {children}
    </section>
  );
}

/**
 * Des lignes à icône, séparées d'un pointillé : le dessin d'une entrée du Journal (`.journal-
 * ligne`). C'est la forme des effets, des calculs, des conditions, tout ce qui se lit en liste.
 */
export function DessousLignes({
  lignes,
}: {
  lignes: readonly { icone?: string; texte: ReactNode }[];
}) {
  return (
    <ul className="dessous-lignes">
      {lignes.map((l, i) => (
        <li className="dessous-ligne" key={i}>
          <span aria-hidden="true">{l.icone ?? "•"}</span>
          <span>{l.texte}</span>
        </li>
      ))}
    </ul>
  );
}
